import { BodyText, Card, Flex, Heading } from '@utilitywarehouse/hearth-react';
import {
  BroadbandMediumIcon,
  ElectricityMediumIcon,
  InsuranceMediumIcon,
  MobileMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { ReactNode } from 'react';

import {
  SlideHeading,
  Terms,
} from '@components/PresentationRebrand/ContentSlide/slides/shared';
import { useBreakpoints } from '@utils/useBreakpoints.ts';

import './styles.scss';
import photo from '/Presentation/AwardWinningServices/photo.png';

type ServiceColour = 'energy' | 'broadband' | 'mobile' | 'insurance';

type Service = {
  colour: ServiceColour;
  icon: ReactNode;
  title: string;
  text: string;
};

const SERVICES: Service[] = [
  {
    colour: 'energy',
    icon: <ElectricityMediumIcon />,
    title: 'Energy',
    text: "UK's cheapest variable energy tariffs when you take any other service.",
  },
  {
    colour: 'broadband',
    icon: <BroadbandMediumIcon />,
    title: 'Broadband',
    text: 'Speeds for all your needs.',
  },
  {
    colour: 'mobile',
    icon: <MobileMediumIcon />,
    title: 'Mobile',
    text: "Save on the UK's best network. And yep - you can keep your number.",
  },
  {
    colour: 'insurance',
    icon: <InsuranceMediumIcon />,
    title: 'Insurance',
    text: 'Five-star-rated Home Insurance (Moneyfacts and Defaqto). and Boiler & Home Cover (Moneyfacts).',
  },
];

export const AwardWinningServices = () => {
  const { breakpointsDown } = useBreakpoints();
  const isMobile = breakpointsDown.sm;
  const isTablet = breakpointsDown.md && !isMobile;
  const isDesktop = !breakpointsDown.md;

  const photoCard = (
    <Card
      variant="emphasis"
      shadowColor="functional"
      colorScheme="neutralStrong"
      className="award-winning-services__photo"
      paddingNone
    >
      <div className="award-winning-services__photo-frame">
        <img
          src={photo}
          alt="A couple relaxing at home with their dog"
          className="award-winning-services__photo-image"
        />
      </div>
    </Card>
  );

  return (
    <Flex
      direction="column"
      gap={{ mobile: '200', tablet: '300' }}
      className="award-winning-services"
    >
      <SlideHeading>Save with our award-winning services</SlideHeading>

      <Flex
        spacing="2xl"
        direction={{ mobile: 'column', desktop: 'row' }}
        alignItems={{ desktop: 'stretch' }}
        className="award-winning-services__content"
      >
        <Flex
          direction="column"
          spacing="xl"
          className="award-winning-services__main"
        >
          <div className="award-winning-services__group">
            {isTablet && photoCard}
            <Flex
              asChild
              direction="column"
              gap={{ mobile: '150', tablet: '300' }}
              className="award-winning-services__services"
            >
              <ul>
                {SERVICES.map(({ colour, icon, title, text }) => (
                  <Flex
                    asChild
                    key={title}
                    alignItems="start"
                    spacing="lg"
                    className="award-winning-services__service"
                  >
                    <li>
                      <span
                        className={`award-winning-services__icon award-winning-services__icon--${colour}`}
                      >
                        {icon}
                      </span>
                      <Flex
                        direction="column"
                        spacing="sm"
                        className="award-winning-services__service-text"
                      >
                        <Heading size="md" asChild>
                          <p>{title}</p>
                        </Heading>
                        <BodyText size={{ mobile: 'md', tablet: 'lg' }}>
                          {text}
                        </BodyText>
                      </Flex>
                    </li>
                  </Flex>
                ))}
              </ul>
            </Flex>
          </div>

          <Card
            variant="emphasis"
            colorScheme="neutralStrong"
            className="award-winning-services__promise-card"
          >
            <Flex
              direction="column"
              spacing="sm"
              className="award-winning-services__promise"
            >
              <Heading size="md" asChild>
                <p>Our promise to you...</p>
              </Heading>
              <BodyText size={{ mobile: 'md', tablet: 'lg' }}>
                If you still don't save after taking three or more qualifying
                services and the Cashback Card, we'll double the difference.
              </BodyText>
            </Flex>
          </Card>
        </Flex>

        {isDesktop && photoCard}
      </Flex>

      <Terms />
    </Flex>
  );
};
